<template>
  <!-- A detail page opened from another record's detail (e.g. UQ → View Appointment): "Back to Universal Quote 5799".
       Also a job opened from "View Linked Jobs" (?from_job=…), even in a new tab: "Back to Service & Repair 10465".
       Same pill as BackToSearch; a plain click goes back in history (keeps that page's scroll). -->
  <router-link v-if="from" v-slot="{ href }" :to="from.path" custom>
    <a :href="href"
      class="inline-flex items-center gap-2 mb-[30px] px-[14px] py-[6px] rounded-full border-0 font-roboto text-[14px] text-[#8479E1] no-underline bg-[#fff] shadow-[0_0_10px_0_#0000001A] hover:bg-[#F3F0FF] transition-colors"
      @click="onClick">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
      Back to {{ from.label }} {{ from.id }}
    </a>
  </router-link>
</template>

<script>
import { isNewTabClick } from "@/helpers/newTabClick";
import { goBackTo } from "@/helpers/historyPositions";

// detail pages: path → name shown on the button
const DETAILS = [
  { re: /^\/quote\/detail\/([^/?#]+)$/,label: "Universal Quote" },
  { re: /^\/appointment\/detail\/([^/?#]+)$/, label: "Appointment" },
  { re: /^\/job\/detail\/([^/?#]+)$/, label: "Service & Repair" },
  { re: /^\/replacement-quote\/detail\/([^/?#]+)$/, label: "Replacement Quote" },
];

const detailOf = (fullPath) => {
  const path = String(fullPath || "").split(/[?#]/)[0];
  for (const d of DETAILS) {
    const m = path.match(d.re);
    if (m) return { path: fullPath, pathOnly: path, label: d.label, id: m[1] };
  }
  return null;
};

export default {
  computed: {
    // previous history entry, when both it and this page are details of different records
    from() {
      if (!detailOf(this.$route.path)) return null;
      const { from_job, is_from_mobile } = this.$route.query;
      if (from_job && /^\/job\/detail\//.test(this.$route.path)) {
        const query = is_from_mobile ? `?is_from_mobile=${encodeURIComponent(is_from_mobile)}` : "";
        return { path: `/job/detail/${from_job}${query}`, label: "Service & Repair", id: from_job, linked: true };
      }
      const prev = detailOf(window.history.state?.back);
      return prev && prev.pathOnly !== this.$route.path ? prev : null;
    },
  },
  // while this button shows, the card's round back button (DetailBackButton, below laptop) hides — one back button
  watch: {
    $route: { immediate: true, handler() { this.$nextTick(() => document.documentElement.classList.toggle("fxBackToDetail", !!this.from)); } },
  },
  beforeUnmount() {
    document.documentElement.classList.remove("fxBackToDetail");
  },
  methods: {
    onClick(e) {
      if (isNewTabClick(e)) return; // browser opens it in a new tab
      e.preventDefault();
      // a linked job may be the first page of the tab: back when the job is in history, else replace
      if (this.from.linked) goBackTo(this.$router, this.from.path);
      else this.$router.back();
    },
  },
};
</script>
